<script>
import axios from 'axios';
import { iAlertMessage, iIcon, iIconButton, iSpinner } from '@inscribe/signal';
import {
  encodeQueryParams,
  getAndSyncQueryParams,
  updateQueryParams,
} from '@/utils/queryParams';
import DatesFilter from '@/V2/components/filters/DatesFilter';
import TagsFilter from '@/V2/components/filters/TagsFilter';
import DocumentTypesFilter from '@/V2/components/filters/DocumentTypesFilter';
import CustomerStatusesFilter from '@/V2/components/filters/CustomerStatusesFilter';
import FileTypesFilter from '@/V2/components/filters/FileTypesFilter';
import IsFraudulentFilter from '@/V2/components/filters/IsFraudulentFilter';
import SortByFilter from '@/V2/components/filters/SortByFilter';
import DocumentItem from '@/V2/documents/document-list/DocumentItem';
import TablePagination from '@/shared/components/TablePagination';

const ITEMS_PER_PAGE = 15;

export default {
  components: {
    iAlertMessage,
    iIcon,
    iIconButton,
    iSpinner,
    DatesFilter,
    TagsFilter,
    DocumentTypesFilter,
    CustomerStatusesFilter,
    FileTypesFilter,
    IsFraudulentFilter,
    SortByFilter,
    DocumentItem,
    TablePagination,
  },
  data() {
    return {
      params: getAndSyncQueryParams({
        fullPath: this.$route.fullPath,
        router: this.$router,
        store: this.$store,
      }),
      isLoading: false,
      data: undefined,
      metadata: undefined,
      error: undefined,
      ITEMS_PER_PAGE,
      fetchAbortController: undefined,
    };
  },

  computed: {
    totalDocuments() {
      return this.metadata?.pagination?.total ?? 0;
    },
    deletedCount() {
      return this.metadata?.deleted_count ?? 0;
    },
    paginationVisible() {
      return this.metadata?.pagination?.total > this.ITEMS_PER_PAGE;
    },
    currentPage() {
      return parseInt(this.params?.page) || 1;
    },
    encodedParams() {
      return encodeQueryParams(this.params);
    },
  },
  watch: {
    '$route.fullPath': {
      handler: function (newFullPath) {
        this.params = getAndSyncQueryParams({
          fullPath: newFullPath,
          router: this.$router,
          store: this.$store,
        });
      },
    },
    params: {
      handler: 'fetchData',
      immediate: true,
    },
  },
  methods: {
    async fetchData() {
      // if there is a previous inflight request, cancel it
      if (this.fetchAbortController) {
        this.fetchAbortController.abort();
      }
      this.fetchAbortController = new AbortController();
      this.data = undefined;
      this.metadata = undefined;
      this.error = undefined;
      this.isLoading = true;
      try {
        const res = await axios.get(
          `/api/v2/documents?response_style=summary&${encodeQueryParams(
            this.params,
          )}`,
          {
            signal: this.fetchAbortController.signal,
          },
        );
        this.data = res.data.data;
        this.metadata = res.data?.metadata;
      } catch (e) {
        if (e.message !== 'canceled') {
          this.error = e.response.data.message;
        }
      } finally {
        this.isLoading = false;
      }
    },
    onPageChange(page) {
      if (page !== this.currentPage) {
        updateQueryParams({
          fullPath: this.$route.fullPath,
          router: this.$router,
          store: this.$store,
          updates: {
            page,
            limit: this.ITEMS_PER_PAGE,
          },
        });
      }
    },
    onDocumentDeleted(documentData) {
      const foundIndex = this.data.findIndex(
        (document) =>
          document.id.toString() === documentData.document.id.toString(),
      );
      if (foundIndex > -1) {
        this.data.splice(foundIndex, 1);
      }
    },
    shouldShowAnalyticsButton() {
      return this.$route.query.return_to === 'analytics';
    },
  },
};
</script>

<template>
  <div class="documents-page__wrapper">
    <div class="documents-page__content">
      <div class="documents-page__header">
        <router-link v-if="shouldShowAnalyticsButton()" to="/analytics">
          <i-icon-button
            data-testid="documents-page-back-button"
            symbol="medium-arrow-left"
            type="transparent"
          />
        </router-link>
        <div class="documents-page__header-title-subtitle">
          <div class="documents-page__header-title">Documents</div>
          <div class="documents-page__header-subtitle">
            View and manage the documents in your workspace
          </div>
        </div>
      </div>

      <div class="documents-page__filters">
        <DatesFilter />
        <CustomerStatusesFilter entity="documents" />
        <IsFraudulentFilter />
        <DocumentTypesFilter />
        <FileTypesFilter />
        <TagsFilter />
      </div>
      <div
        class="documents-page__sort_and_count d-flex flex-row align-items-space-between"
      >
        <SortByFilter />
        <div
          class="count i-text-neutral-110 i-text-subheading-3 d-flex align-items-center"
        >
          <span v-if="!isLoading">
            {{ totalDocuments }}
            {{ totalDocuments === 1 ? 'document' : 'documents' }}
          </span>
        </div>
      </div>

      <i-alert-message
        v-if="deletedCount > 0"
        class="documents-page__alert"
        type="outline"
        size="sm"
        heading="Some documents cannot be displayed because they have been deleted."
        message="These documents are still reflected in the Analytics view."
      ></i-alert-message>

      <div
        v-if="isLoading"
        class="documents-page__loading d-flex flex-row justify-content-center"
      >
        <i-spinner></i-spinner>
      </div>

      <div
        v-if="!isLoading && (!data || !data.length)"
        class="documents-page__empty d-flex flex-column align-items-center justify-content-center i-mt-14"
      >
        <div
          class="documents-page__empty__icon_wrapper d-flex flex-column align-items-center justify-content-center i-mb-8"
        >
          <i-icon
            class="documents-page__empty__icon i-text-primary-110"
            symbol="large-file-text-search"
            :size="46"
          />
        </div>

        <div
          class="documents-page__empty__title i-text-neutral-120 i-text-heading-5 i-mb-4"
        >
          No documents matched the selected filters
        </div>

        <div
          class="documents-page__empty__message i-text-neutral-110 i-text-body-2"
        >
          Matching documents will appear here
        </div>
      </div>

      <!-- documents list -->
      <div v-if="!isLoading && data?.length" class="documents-page__list">
        <document-item
          v-for="(docItem, dIndex) in data"
          :key="docItem + dIndex"
          :document="docItem"
          data-testid="document-switcher-item"
          :options="{
            actionMenu: true,
            checks: true,
            currentDocLabel: false,
            tags: true,
            trustScoreInfo: true,
            clickable: true,
            return_to: encodeURIComponent(`/documents?${encodedParams}`),
          }"
          @document-deleted="onDocumentDeleted"
        />
      </div>

      <!-- pagination -->
      <div
        v-if="paginationVisible"
        class="documents-page__pagination d-flex flex-row align-items-center justify-content-center i-py-10"
      >
        <table-pagination
          :total-rows="totalDocuments"
          :per-page="ITEMS_PER_PAGE"
          :current-page="currentPage"
          @page-change="onPageChange"
        >
        </table-pagination>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.documents-page {
  &__wrapper {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    padding: map-get($increments, 11) map-get($increments, 12);
  }

  &__sort_and_count {
    width: 100%;
    justify-content: space-between;
  }

  &__content {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: map-get($increments, 10);
    width: 100%;
    max-width: $desktop;
  }

  &__header {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: map-get($increments, 6);
  }

  &__header-title-subtitle {
    display: flex;
    flex-direction: column;
  }

  &__header-title {
    font-size: map-get($text-size-heading, 6);
    font-weight: map-get($text-weights, 'semibold');
  }

  &__header-subtitle {
    font-size: map-get($text-size-standard, 2);
    color: $light-neutral-110;

    @include respond-to('small') {
      display: none;
    }
  }

  &__filters {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
    gap: map-get($increments, 3);
  }

  &__list {
    $borderRadius: map-get($radii, '2xl');

    border: 1px solid $light-neutral-60;
    border-radius: $borderRadius;
    width: 100%;
  }

  &__pagination {
    position: fixed;
    bottom: 0;
    left: 240px;
    right: 0;
    z-index: 1000;
    background: white;
    border-top: 1px solid $light-neutral-60;

    .pagination {
      width: 1200px;
      margin-bottom: 0;
    }
  }

  &__loading {
    width: 100%;
    min-height: 200px;
  }

  &__empty {
    min-width: 200px;
    width: 100%;

    &__icon_wrapper {
      width: 128px;
      height: 128px;
      border-radius: 100%;
      background: $light-primary-20;
    }
  }
}
</style>
